import axios, { type InternalAxiosRequestConfig } from 'axios';

/**
 * 쿠키에서 값을 가져오는 헬퍼 함수
 * @param name 쿠키 이름
 * @returns 쿠키 값 또는 null
 */
function getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop()?.split(';').shift() || null;
    }
    return null;
}

/**
 * XSRF-TOKEN이 유효한지 확인
 * @returns XSRF-TOKEN이 존재하고 비어있지 않으면 true
 */
export function hasValidXSRFToken(): boolean {
    const token = getCookie('XSRF-TOKEN');
    return token !== null && token.trim() !== '';
}

/**
 * CSRF 쿠키를 갱신하는 함수
 * @returns Promise<boolean> 성공 여부
 */
export async function refreshCSRFCookie(): Promise<boolean> {
    try {
        const response = await axios.get('/sanctum/csrf-cookie', {
            withCredentials: true,
        });

        // 응답 상태 코드 확인
        if (response.status !== 204 && response.status !== 200) {
            return false;
        }

        return hasValidXSRFToken();
    } catch (error) {
        console.error('CSRF 쿠키 갱신 실패:', error);
        return false;
    }
}

/**
 * XSRF-TOKEN이 유효한지 확인하고, 유효하지 않으면 갱신
 * @returns Promise<boolean> 토큰이 유효하거나 갱신에 성공하면 true
 */
export async function ensureValidXSRFToken(): Promise<boolean> {
    if (hasValidXSRFToken()) {
        return true;
    }

    return await refreshCSRFCookie();
}

/**
 * axios request 인터셉터에서 사용할 XSRF-TOKEN 확인 함수
 * POST, PUT, PATCH, DELETE 메서드인 경우에만 토큰을 확인합니다.
 * @param config axios request config
 * @returns Promise<InternalAxiosRequestConfig>
 */
export async function axiosXSRFTokenInterceptor(
    config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
    const method = config.method?.toUpperCase();
    if (method !== 'GET') {
        await ensureValidXSRFToken();
    }
    return config;
}
